import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import { useTranslation } from "react-i18next";
import {
  primaryColor,
  secondaryColor,
  tooltipContentStyle,
  tooltipCursorStyle,
} from "utils/dashboardUtils";
import { formatLabel, isDesktop } from "utils/utils";
import DashboardFilter from "../DashboardFilter";
import CustomerLeadsFilter from "../CustomerLeadsFilter";

const CustomizedAxisTick = ({ x, y, payload, index, data, shouldRotate }) => {
  const mainLabel = payload.value ?? "";
  const subLabel = data?.[index]?.subLabel ?? "";

  return (
    <g transform={`translate(${x},${y})`}>
      <text
        x={0}
        y={0}
        dy={14}
        textAnchor={shouldRotate ? "end" : "middle"}
        fill={secondaryColor}
        fontSize={12}
        fontWeight={600}
        transform={shouldRotate ? "rotate(-35)" : ""}
      >
        {mainLabel}
      </text>

      {subLabel && (
        <text
          x={0}
          y={0}
          dy={shouldRotate ? 28 : 30}
          textAnchor={shouldRotate ? "end" : "middle"}
          fill={secondaryColor}
          fontSize={10}
          fontWeight={400}
          transform={shouldRotate ? "rotate(-35)" : ""}
        >
          {subLabel}
        </text>
      )}
    </g>
  );
};
const LeadsGrowthChart = ({
  membershipGrowth,
  activeFilterType,
  className = "",
  showLeadsPageFilter = false,
  dates,
  setDates,
  setActiveFilter,
}) => {
  const { t } = useTranslation();

  const MEMBERSHIP_GROWTH_DATA = membershipGrowth?.map((item) => ({
    month: item.label,
    subLabel: item.subLabel,
    newLeads: item.newLeads,
  }));

  const getSubtitle = () => {
    switch (activeFilterType) {
      case "DAY":
        return t("Total new leads for selected month");
      case "WEEK":
        return t("Weekly leads overview");
      case "MONTH":
        return t("Monthly leads overview for current year");
      default:
        return t("Total active members");
    }
  };
  const shouldRotate = !isDesktop || MEMBERSHIP_GROWTH_DATA?.length > 6;

  return (
    <div className={`membership_growth dashboard_common_card ${className}`}>
      {/* Title */}
      <div className="d-flex justify-content-between align-items-center mb-5 flex-wrap membership_stat">
        <div>
          <h3 className="fs_20">{t("Leads Growth")} </h3>
          <p className="page_sub_title mt-2">{getSubtitle()}</p>
        </div>
        <CustomerLeadsFilter
          dates={dates}
          setDates={setDates}
          setActiveFilter={setActiveFilter}
          className={"leads_stats mb-0"}
          selectDateLabel={false}
          selectTypeLabel={false}
        />
        {/* Legend */}
        <div className="legends">
          <div className="inner_legend">
            <span className="dot new_dot new_lead_dot" />
            {t("New Leads")}
          </div>
        </div>
      </div>

      {/* Chart */}
      <ResponsiveContainer width="100%" height={300}>
        <BarChart
          data={MEMBERSHIP_GROWTH_DATA}
          margin={{ top: 10, right: 30, left: 20, bottom: 10 }}
          barCategoryGap="30%"
          barGap={4}
        >
          <XAxis
            dataKey="month"
            axisLine={false}
            tickLine={false}
            interval={isDesktop ? 0 : 1}
            height={shouldRotate ? 60 : 50}
            tick={(props) => (
              <CustomizedAxisTick
                {...props}
                data={MEMBERSHIP_GROWTH_DATA}
                shouldRotate={shouldRotate}
              />
            )}
            tickMargin={shouldRotate ? 0 : 15}
            tickFormatter={
              shouldRotate ? undefined : (label) => label
              // : (label) => formatLabel(label, activeFilterType)
            }
          />

          <YAxis hide />

          <Tooltip
            cursor={tooltipCursorStyle}
            formatter={(value, name) => [value, t("New Leads")]}
            labelFormatter={(label) => label}
            // labelFormatter={(label) => formatLabel(label, activeFilterType)}
            contentStyle={tooltipContentStyle}
            labelStyle={{ fontWeight: 600 }}
          />

          <CartesianGrid
            vertical={false}
            stroke="#E6EAF0"
            strokeDasharray="0"
          />

          <Bar dataKey="newLeads" fill={`#38BDF8`} radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default LeadsGrowthChart;
